import { api } from './apiService';
import type { Payout } from '@/types/payout';

// Normalise le format date PostgreSQL ("2026-03-31 23:59:00+00") → ISO UTC ("2026-03-31T23:59:00.000Z")
function normalizeDate(raw: string): string {
  return new Date(raw.replace(' ', 'T').replace(/(\+\d{2})$/, '$1:00')).toISOString();
}

// Map from DB snake_case row → frontend Payout type
function rowToPayout(row: Record<string, unknown>): Payout {
  return {
    id: row.id as string,
    type: row.type as Payout['type'],
    date: normalizeDate(row.date as string),
    createdAt: row.created_at as string,
    crypto: row.crypto_id
      ? { id: row.crypto_id as string, symbol: row.crypto_symbol as string, name: row.crypto_name as string }
      : undefined,
    amount: row.amount != null ? Number(row.amount) : undefined,
    unitPrice: row.unit_price != null ? Number(row.unit_price) : undefined,
    totalValue: row.total_value != null ? Number(row.total_value) : undefined,
    sentCrypto: row.sent_crypto_id
      ? { id: row.sent_crypto_id as string, symbol: row.sent_crypto_symbol as string, name: row.sent_crypto_name as string }
      : undefined,
    sentAmount: row.sent_amount != null ? Number(row.sent_amount) : undefined,
    sentUnitPrice: row.sent_unit_price != null ? Number(row.sent_unit_price) : undefined,
    sentTotalValue: row.sent_total_value != null ? Number(row.sent_total_value) : undefined,
    receivedCrypto: row.received_crypto_id
      ? { id: row.received_crypto_id as string, symbol: row.received_crypto_symbol as string, name: row.received_crypto_name as string }
      : undefined,
    receivedAmount: row.received_amount != null ? Number(row.received_amount) : undefined,
    receivedUnitPrice: row.received_unit_price != null ? Number(row.received_unit_price) : undefined,
    receivedTotalValue: row.received_total_value != null ? Number(row.received_total_value) : undefined,
    fees: row.fees != null ? Number(row.fees) : undefined,
    feesCurrency: row.fees_currency as string | undefined,
    platform: row.platform as string | undefined,
    notes: row.notes as string | undefined,
    portfolioValueAtSale: row.portfolio_value_at_sale != null ? Number(row.portfolio_value_at_sale) : undefined,
    importBatchId: row.import_batch_id as string | undefined,
  };
}

// Map from frontend Payout → API payload (snake_case)
function payoutToPayload(payout: Payout): Record<string, unknown> {
  return {
    id: payout.id,
    type: payout.type,
    date: payout.date,
    crypto_id: payout.crypto?.id,
    crypto_symbol: payout.crypto?.symbol,
    crypto_name: payout.crypto?.name,
    amount: payout.amount,
    unit_price: payout.unitPrice,
    total_value: payout.totalValue,
    sent_crypto_id: payout.sentCrypto?.id,
    sent_crypto_symbol: payout.sentCrypto?.symbol,
    sent_crypto_name: payout.sentCrypto?.name,
    sent_amount: payout.sentAmount,
    sent_unit_price: payout.sentUnitPrice,
    sent_total_value: payout.sentTotalValue,
    received_crypto_id: payout.receivedCrypto?.id,
    received_crypto_symbol: payout.receivedCrypto?.symbol,
    received_crypto_name: payout.receivedCrypto?.name,
    received_amount: payout.receivedAmount,
    received_unit_price: payout.receivedUnitPrice,
    received_total_value: payout.receivedTotalValue,
    fees: payout.fees,
    fees_currency: payout.feesCurrency,
    platform: payout.platform,
    notes: payout.notes,
    portfolio_value_at_sale: payout.portfolioValueAtSale,
  };
}

export const payoutApiService = {
  async list(): Promise<Payout[]> {
    const { data } = await api.get<Record<string, unknown>[]>('/payouts');
    return data.map(rowToPayout);
  },

  async create(payout: Payout): Promise<Payout> {
    const { data } = await api.post<Record<string, unknown>>('/payouts', payoutToPayload(payout));
    return rowToPayout(data);
  },

  async update(payout: Payout): Promise<Payout> {
    const { data } = await api.put<Record<string, unknown>>(
      `/payouts/${payout.id}`,
      payoutToPayload(payout)
    );
    return rowToPayout(data);
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/payouts/${id}`);
  },

  async deleteAll(): Promise<void> {
    await api.delete('/payouts');
  },

  async bulkImport(
    payouts: Payout[],
    options?: { settings?: unknown; miningConfigs?: unknown; batchLabel?: string; batchSource?: string }
  ): Promise<{ imported: number; skipped: number; batch_id?: string }> {
    const { data } = await api.post<{ imported: number; skipped: number; batch_id?: string }>('/payouts/import', {
      payouts: payouts.map(payoutToPayload),
      settings: options?.settings,
      mining_configs: options?.miningConfigs,
      batch_label: options?.batchLabel,
      batch_source: options?.batchSource,
    });
    return data;
  },

  async exportAll(): Promise<{
    payouts: Payout[];
    settings: unknown;
    miningConfigs: unknown;
    exportDate: string;
    version: string;
  }> {
    const { data } = await api.get<{
      payouts: Record<string, unknown>[];
      settings: unknown;
      miningConfigs: unknown;
      exportDate: string;
      version: string;
    }>('/payouts/export');

    return {
      payouts: data.payouts.map(rowToPayout),
      settings: data.settings,
      miningConfigs: data.miningConfigs,
      exportDate: data.exportDate,
      version: data.version,
    };
  },
};
